class ParallaxSection {
    constructor(element) {
        this.element = element;
        this.background = element.querySelector('.parallax__background');
        this.backgroundImage = element.querySelector('.parallax__background-image');

        this.intensity = Number.parseFloat(element.dataset.parallaxIntensity) / 100 || 0.5;
        this.enabled = element.dataset.parallaxEnabled === 'true';

        this.disableOnMobile = element.classList.contains('parallax-section--disable-mobile');
        this.isMobile = window.matchMedia('(max-width: 749px)').matches;

        if (!this.enabled || !this.backgroundImage) {
            return;
        }

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);

        this.init();
    }

    init() {
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.startParallax();
                    } else {
                        this.stopParallax();
                    }
                });
            },
            { threshold: 0, rootMargin: '50px 0px 50px 0px' }
        );

        this.observer.observe(this.element);

        window.addEventListener('resize', this.onResize);

        this.calculateBounds();
    }

    calculateBounds() {
        const rect = this.element.getBoundingClientRect();
        this.elementTop = rect.top + window.scrollY;
        this.elementHeight = rect.height;
        this.windowHeight = window.innerHeight;
    }

    startParallax() {
        if (this.isRunning) return;
        if (this.disableOnMobile && this.isMobile) return;

        this.isRunning = true;
        this.calculateBounds();
        window.addEventListener('scroll', this.onScroll, { passive: true });

        this.updateParallax();
    }

    stopParallax() {
        if (!this.isRunning) return;

        this.isRunning = false;
        window.removeEventListener('scroll', this.onScroll);

        if (this.backgroundImage) {
            this.backgroundImage.style.transform = '';
        }
    }

    onScroll() {
        if (!this.ticking) {
            requestAnimationFrame(() => {
                this.updateParallax();
                this.ticking = false;
            });
            this.ticking = true;
        }
    }

    updateParallax() {
        if (!this.backgroundImage || !this.isRunning) return;

        const scrollY = window.scrollY;
        const elementTop = this.elementTop;
        const elementHeight = this.elementHeight;
        const windowHeight = this.windowHeight;

        const startPoint = elementTop - windowHeight;
        const endPoint = elementTop + elementHeight;
        const scrollRange = endPoint - startPoint;
        const currentProgress = (scrollY - startPoint) / scrollRange;

        const progress = Math.max(0, Math.min(1, currentProgress));

        const maxOffset = elementHeight * 0.3 * this.intensity;
        const offset = (progress - 0.5) * maxOffset;

        this.backgroundImage.style.transform = `translate3d(0, ${offset}px, 0)`;
    }

    onResize() {
        this.isMobile = window.matchMedia('(max-width: 749px)').matches;
        this.calculateBounds();

        if (this.disableOnMobile && this.isMobile && this.isRunning) {
            this.stopParallax();
        }
    }

    destroy() {
        this.stopParallax();
        window.removeEventListener('resize', this.onResize);
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

function initParallaxSections() {
    const sections = document.querySelectorAll('.parallax-section[data-parallax-enabled="true"]');

    sections.forEach((section) => {
        if (section.parallaxInstance) return;
        section.parallaxInstance = new ParallaxSection(section);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initParallaxSections);
} else {
    initParallaxSections();
}

if (Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
        const section = event.target.querySelector('.parallax-section');
        if (section) {
            section.parallaxInstance = new ParallaxSection(section);
        }
    });

    document.addEventListener('shopify:section:unload', (event) => {
        const section = event.target.querySelector('.parallax-section');
        if (section && section.parallaxInstance) {
            section.parallaxInstance.destroy();
        }
    });
}
